import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Carlos Mendes',
    role: 'Product Designer',
    avatar: 'CM',
    content: 'A qualidade das impressões é excepcional! Usei para prototipar meus designs e o resultado superou todas as expectativas. Entrega rápida e atendimento impecável.',
    rating: 5,
  },
  {
    name: 'Ana Paula Silva',
    role: 'Arquiteta',
    avatar: 'AS',
    content: 'Precisava de maquetes detalhadas para uma apresentação importante. A Fort3D entregou com precisão milimétrica e acabamento profissional. Recomendo!',
    rating: 5,
  },
  {
    name: 'Bruno Costa',
    role: 'Maker & Youtuber',
    avatar: 'BC',
    content: 'Como maker, já testei várias empresas de impressão 3D. A Fort3D está no topo da lista em relação custo-benefício e qualidade. Os suportes para meu setup ficaram perfeitos!',
    rating: 5,
  },
  {
    name: 'Juliana Ferreira',
    role: 'Empresária',
    avatar: 'JF',
    content: 'Encomendei peças decorativas para minha loja. Cada detalhe foi cuidadosamente executado. Minhas clientes adoram! Já fiz várias recompras.',
    rating: 5,
  },
  {
    name: 'Ricardo Almeida',
    role: 'Engenheiro',
    avatar: 'RA',
    content: 'A precisão das peças técnicas é impressionante. Usei para peças de reposição em projetos e a tolerância foi perfeita. Excelente trabalho!',
    rating: 5,
  },
  {
    name: 'Mariana Santos',
    role: 'Estudante de Design',
    avatar: 'MS',
    content: 'Estou começando na área de design de produtos e a Fort3D tem sido fundamental para materializar meus projetos acadêmicos. Preço justo e qualidade top!',
    rating: 5,
  },
];

export function TestimonialsSection() {
  return (
    <section className="py-24 bg-gray-950 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-4">
            Depoimentos
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            O que nossos{' '}
            <span className="bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
              clientes
            </span>{' '}
            dizem
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Junte-se a milhares de clientes satisfeitos que confiam na Fort3D 
            para transformar suas ideias em realidade
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <motion.div
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
                className="h-full p-6 rounded-2xl bg-gray-900/50 backdrop-blur-sm border border-white/5 hover:border-orange-500/20 transition-colors"
              >
                {/* Quote Icon */}
                <div className="mb-4">
                  <Quote className="w-8 h-8 text-orange-500/30" />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Content */}
                <p className="text-gray-300 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-16 flex flex-wrap justify-center items-center gap-8"
        >
          <div className="flex items-center gap-2 text-gray-500">
            <div className="flex -space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="font-medium">4.9/5</span>
            <span className="text-sm">baseado em 2.000+ avaliações</span>
          </div>
          
          <div className="h-6 w-px bg-gray-800 hidden sm:block" />
          
          <div className="text-gray-500">
            <span className="font-medium text-white">+15.000</span> produtos entregues
          </div>
          
          <div className="h-6 w-px bg-gray-800 hidden sm:block" />
          
          <div className="text-gray-500">
            <span className="font-medium text-white">99%</span> de clientes satisfeitos
          </div>
        </motion.div>
      </div>
    </section>
  );
}
